import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Authorization, ServiceSession } from '../types';
import {
  BRIDGE_ALREADY_REPLACED_MESSAGE,
  IMPORT_OVERWRITE_NOTE,
  STALE_AUTHORIZATION_MESSAGE,
  bridgeCompatibilityProblems,
  displayAvailableUnits,
  isImportedAuthorization,
  isOpenManualBridge,
  normalizeBalance
} from '../utils/bridgeAuthorizationRules';

type Mode = 'replace' | 'correct';

function serviceIdsFor(services: ServiceSession[], authId: string): string[] {
  return services.filter((service) => service.auth_id === authId).map((service) => service.id).sort();
}

function BalanceLine({ label, auth }: { label: string; auth: Authorization }) {
  const balance = normalizeBalance(auth);
  const available = displayAvailableUnits(balance.totalUnits, balance.unitsUsed, balance.availableUnits);
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
      <div className="font-semibold text-slate-900">Auth #{auth.auth_number}</div>
      <div className="mt-1 font-mono">{label} · {auth.auth_type || 'UNMAPPED'} · {auth.source}</div>
      <div className="mt-1 font-mono">
        {balance.unitsUsed} used / {balance.totalUnits} total · {available} available
      </div>
    </div>
  );
}

function ServiceList({ services }: { services: ServiceSession[] }) {
  if (services.length === 0) {
    return <p className="text-xs text-slate-500">No services are linked to this authorization.</p>;
  }
  return (
    <ul className="space-y-1 text-xs text-slate-700">
      {services.map((service) => (
        <li key={service.id} className="font-mono">
          {service.dos} · {service.service_code} · {service.status} · {service.id}
        </li>
      ))}
    </ul>
  );
}

export function BridgeAuthorizationPanel({
  mode,
  clientId,
  initialAuthId,
  authorizations,
  services,
  refreshNonce,
  onClose,
  onChartUpdated,
  onRefreshEvidence
}: {
  mode: Mode;
  clientId: string;
  initialAuthId: string;
  authorizations: Authorization[];
  services: ServiceSession[];
  refreshNonce: number;
  onClose: () => void;
  onChartUpdated: () => Promise<void> | void;
  onRefreshEvidence: () => Promise<void> | void;
}) {
  const [snapshotAuths, setSnapshotAuths] = useState(authorizations);
  const [snapshotServices, setSnapshotServices] = useState(services);
  const [bridgeId, setBridgeId] = useState(initialAuthId);
  const [replacementId, setReplacementId] = useState('');
  const [unitsUsed, setUnitsUsed] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const authorizationsRef = useRef(authorizations);
  const servicesRef = useRef(services);
  authorizationsRef.current = authorizations;
  servicesRef.current = services;

  useEffect(() => {
    if (refreshNonce === 0) return;
    setSnapshotAuths(authorizationsRef.current);
    setSnapshotServices(servicesRef.current);
    setNotice('');
  }, [refreshNonce]);

  const manuals = snapshotAuths.filter((auth) => isOpenManualBridge(auth));
  const imported = snapshotAuths.filter((auth) => isImportedAuthorization(auth));
  const bridge = manuals.find((auth) => auth.id === bridgeId) || manuals[0];
  const selectedReplacement = imported.find((auth) => auth.id === replacementId) || null;
  const correctAuth = snapshotAuths.find((auth) => auth.id === initialAuthId && isImportedAuthorization(auth)) || null;

  useEffect(() => {
    if (mode === 'replace' && !replacementId && imported[0]) {
      setReplacementId(imported[0].id);
    }
  }, [mode, replacementId, imported]);

  const linkedToBridge = useMemo(
    () => snapshotServices.filter((service) => bridge && service.auth_id === bridge.id),
    [snapshotServices, bridge]
  );
  const linkedToCorrect = useMemo(
    () => snapshotServices.filter((service) => correctAuth && service.auth_id === correctAuth.id),
    [snapshotServices, correctAuth]
  );
  const problems = bridge && selectedReplacement
    ? bridgeCompatibilityProblems(bridge, selectedReplacement, linkedToBridge)
    : [];

  const save = async () => {
    setSaving(true);
    setNotice('');
    try {
      const count = unitsUsed.trim();
      if (!/^(0|[1-9]\d*)$/.test(count)) {
        setNotice('Enter the current used count as a whole number.');
        return;
      }
      const response = mode === 'replace'
        ? await fetch(`/api/clients/${clientId}/authorizations/replace-bridge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bridgeId: bridge?.id,
            replacementId: selectedReplacement?.id,
            unitsUsed: Number(count),
            evidence: bridge && selectedReplacement ? {
              bridge: normalizeBalance(bridge),
              replacement: normalizeBalance(selectedReplacement),
              serviceIds: serviceIdsFor(snapshotServices, bridge.id)
            } : undefined
          })
        })
        : await fetch(`/api/clients/${clientId}/authorizations/${correctAuth?.id}/correct-usage`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            unitsUsed: Number(count),
            evidence: correctAuth ? {
              ...normalizeBalance(correctAuth),
              serviceIds: serviceIdsFor(snapshotServices, correctAuth.id)
            } : undefined
          })
        });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setNotice(body.error || STALE_AUTHORIZATION_MESSAGE);
        return;
      }
      setNotice(body.message || (mode === 'replace' ? BRIDGE_ALREADY_REPLACED_MESSAGE : 'Saved.'));
      await onChartUpdated();
    } catch {
      setNotice('The authorization change was not saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-3">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white border border-slate-200 shadow-xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold text-slate-900">
              {mode === 'replace' ? 'Replace bridge' : 'Correct current usage'}
            </h2>
            <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">{IMPORT_OVERWRITE_NOTE}</p>
          </div>
          <button type="button" onClick={onClose} className="text-xs font-semibold text-slate-500">Close</button>
        </div>

        {mode === 'replace' ? (
          <>
            <label className="block text-[11px] font-semibold text-slate-600">
              Manual bridge
              <select
                value={bridge?.id || ''}
                onChange={(event) => setBridgeId(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              >
                {manuals.map((auth) => (
                  <option key={auth.id} value={auth.id}>#{auth.auth_number} · {auth.auth_type} · {auth.units_used} used</option>
                ))}
              </select>
            </label>
            <label className="block text-[11px] font-semibold text-slate-600">
              Imported authorization
              <select
                value={selectedReplacement?.id || ''}
                onChange={(event) => setReplacementId(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              >
                {imported.length === 0 && <option value="">No imported authorization</option>}
                {imported.map((auth) => (
                  <option key={auth.id} value={auth.id}>#{auth.auth_number} · {auth.auth_type} · {auth.units_used} used</option>
                ))}
              </select>
            </label>
            {bridge && <BalanceLine label="Bridge" auth={bridge} />}
            {selectedReplacement && <BalanceLine label="Imported" auth={selectedReplacement} />}
            <div>
              <div className="text-[11px] font-semibold text-slate-600 mb-1">Services on the bridge</div>
              <ServiceList services={linkedToBridge} />
            </div>
            {problems.length > 0 && (
              <p className="text-xs text-rose-700">{problems[0]}</p>
            )}
          </>
        ) : correctAuth ? (
          <>
            <BalanceLine label="Imported authorization" auth={correctAuth} />
            <div>
              <div className="text-[11px] font-semibold text-slate-600 mb-1">Linked services</div>
              <ServiceList services={linkedToCorrect} />
            </div>
          </>
        ) : (
          <p className="text-xs text-slate-600">This authorization is not an imported authorization.</p>
        )}

        <label className="block text-[11px] font-semibold text-slate-600">
          Current used count
          <input
            value={unitsUsed}
            onChange={(event) => setUnitsUsed(event.target.value)}
            inputMode="numeric"
            className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm font-mono"
          />
        </label>
        <p className="text-[11px] text-slate-500">
          {mode === 'replace'
            ? 'This sets the imported authorization’s used count. It does not add the bridge balance, and it does not copy WellSky identifiers onto the bridge.'
            : 'This sets the used count on this authorization. It does not move services or reopen a bridge.'}
        </p>
        {notice && <p className="text-xs font-medium text-slate-800" role="status">{notice}</p>}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={saving || (mode === 'replace' && (!bridge || !selectedReplacement || problems.length > 0))}
            onClick={save}
            className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-bold disabled:opacity-40"
          >
            {mode === 'replace' ? 'Save replacement' : 'Save used count'}
          </button>
          <button
            type="button"
            onClick={() => { void onRefreshEvidence(); }}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-700"
          >
            Refresh authorization
          </button>
        </div>
      </div>
    </div>
  );
}
